import React, { useCallback, useEffect, useRef, useState } from 'react';
import WindowPortal from './WindowPortal';
import DeviceOfflineOverlay from './DeviceOfflineOverlay';
import useHostReachability from '../utils/useHostReachability';
import {
	resolveStream,
	endStreamSession,
	restartSunshine,
} from '../utils/sunshine';

// In-browser Sunshine stream, opened in its own popup window (WindowPortal,
// same pattern as VNC) so the dashboard stays usable behind it. moonlight-web
// is served same-origin under /sunshine/ through nginx (auth via the
// dashboard JWT cookie) — the popup inherits the opener's base URL and
// origin, so the relative iframe src and contentDocument styling both still
// work. The iframe only ever loads its bare stream page — pairing, progress
// and errors are all rendered in dashboard chrome here instead of
// moonlight-web's UI.

const STAGE_TEXT = {
	checking: 'Checking pairing status…',
	pairing: 'Pairing with Sunshine on the devkit…',
	starting: 'Starting desktop stream…',
};

// The iframe is same-origin, so we can restyle moonlight-web's stream page.
// Strategy: hide its overlay UI entirely (the dashboard title bar is the
// chrome), leave only the video on a flat black surround, and dress any
// transient dialogs/toasts it shows as standard dashboard white modals.
// !important throughout because the page swaps its theme <link> at runtime.
const DASHBOARD_THEME_CSS = `
* { text-shadow: none !important; box-shadow: none !important; }
:root {
  --bg-0: #000 !important; --bg-1: #000 !important;
  --bg-2: #000 !important; --bg-3: #000 !important;
  --accent-cyan: #2563eb !important; --accent-cyan-2: #2563eb !important;
  --accent-cyan-light: #3b82f6 !important;
  --glow-cyan: 0 0 0 rgba(0,0,0,0) !important;
  --glow-cyan-bright: 0 0 0 rgba(0,0,0,0) !important;
}
html, body, body::before { background: #000 !important; }
/* Dashboard owns the chrome — hide moonlight-web's overlay UI. The sidebar
   (connection log, stream settings) stays available behind the title bar's
   Details toggle, which sets .mw-details on <html>. */
html:not(.mw-details) #sidebar-root { display: none !important; }
.stream-keyboard-floating-button { display: none !important; }
/* Details sidebar -> dashboard dark-panel style */
.sidebar-background, .sidebar-content {
  background: rgba(15,23,42,0.95) !important;
  border: 1px solid rgba(51,65,85,0.5) !important;
  border-radius: 1rem !important;
}
.sidebar-content, .sidebar-content * { color: #e5e7eb !important; }
.sidebar-button {
  background: rgba(15,23,42,0.9) !important;
  border: 1px solid rgba(51,65,85,0.5) !important;
}
/* Stats overlay: stock is loose cyan text over the video — pin it as a
   compact dashboard panel top-right (sidebar lives on the left) */
.video-stats {
  position: fixed !important;
  top: 12px !important;
  right: 12px !important;
  left: auto !important;
  width: auto !important;
  max-width: 400px !important;
  max-height: calc(100vh - 90px) !important;
  overflow: hidden !important;
  background: rgba(15,23,42,0.92) !important;
  border: 1px solid rgba(51,65,85,0.5) !important;
  border-radius: 0.75rem !important;
  padding: 12px 14px !important;
  color: #e5e7eb !important;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace !important;
  font-size: 11px !important;
  line-height: 1.6 !important;
  white-space: pre-line !important;
  z-index: 40 !important;
}
/* Transient dialogs/toasts -> dashboard white modal style */
.modal-background { background: rgba(0,0,0,0.5) !important; }
.modal-content, .context-menu-background, .toast, .notification-element {
  background: #fff !important; color: #111827 !important;
  border: 1px solid #e5e7eb !important; border-radius: 0.75rem !important;
  box-shadow: 0 25px 50px -12px rgba(0,0,0,0.4) !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
}
.modal-content *, .context-menu-background *, .toast *, .notification-element * {
  color: #111827 !important;
}
button {
  background-color: #2563eb !important; color: #fff !important;
  border: none !important; border-radius: 0.5rem !important;
  font-weight: 500 !important;
}
button:hover:not(:disabled) { background-color: #1d4ed8 !important; }
button:disabled { opacity: 0.5 !important; }
.modal-content button, .toast button, .notification-element button { color: #fff !important; }
input, select, textarea {
  background: #fff !important; color: #111827 !important;
  border: 1px solid #d1d5db !important; border-radius: 0.5rem !important;
}
input[type="checkbox"], input[type="range"] { accent-color: #2563eb !important; }
select option { color: #111827 !important; background: #fff !important; }
`;

const SunshineView = ({ devkit, admin = false, onClose, deviceOnline = true }) => {
	const ip = devkit?.tailscale?.ipv4?.[0];
	const deviceId = devkit?.devkit_id ?? devkit?.tailscale?.name?.slice(0, 4) ?? '';
	// Header/window title: "Moonlight Stream — <device id> — <vessel name>"
	// (vessel name omitted when the devkit doesn't have one).
	const name = [deviceId, devkit?.vessel_name].filter(Boolean).join(' — ');

	const [stage, setStage] = useState('checking');
	const [error, setError] = useState(null);
	const [streamPath, setStreamPath] = useState(null);
	const [hostId, setHostId] = useState(null);
	const [ending, setEnding] = useState(false);
	const [showDetails, setShowDetails] = useState(false);
	const [restarting, setRestarting] = useState(false);
	const [streamStalled, setStreamStalled] = useState(false);
	const iframeRef = useRef(null);
	const autoReconnectsRef = useRef(0);

	// Fast offline detection: WebRTC media halts within ~1s of the device
	// dropping, so "decoded frames stopped advancing" beats any status poll.
	// The iframe is same-origin, so we can watch moonlight-web's <video>
	// directly. Stall time only accumulates while the popup is visible —
	// hidden windows throttle decoding and would false-positive.
	// Tick on the POPUP's timers, not this (dashboard) tab's: users work in
	// the popup with the dashboard tab hidden, and Chrome throttles a hidden
	// tab to one timer wake-up per minute after ~60s — measured on Chrome
	// 155, a dead stream took 120s+ to flag on dashboard timers vs ~8s on the
	// popup's (Oct 2026: frozen 1002 stream, no banner after 10+ minutes).
	useEffect(() => {
		if (!streamPath) {
			setStreamStalled(false);
			return undefined;
		}
		const STALL_MS = 5000;
		const HEALTHY_MS = 60000; // this long streaming re-arms auto-reconnect
		let lastSample = null;
		let sawMedia = false;
		let mediaSince = null;
		let stallSince = null;
		let watched = null; // <video> whose presented frames we're counting
		let presented = 0;
		const timers = iframeRef.current?.ownerDocument?.defaultView || window;
		const iv = timers.setInterval(() => {
			let doc;
			try {
				doc = iframeRef.current?.contentDocument;
			} catch {
				return; // cross-origin — nothing to watch
			}
			const video = doc?.querySelector('video');
			if (!video) return;
			if (doc.visibilityState !== 'visible') {
				stallSince = null;
				return;
			}
			// Count presented frames with requestVideoFrameCallback — it stops
			// the moment frames stop in both Chrome and Firefox. Firefox (158)
			// reports totalVideoFrames as 0 forever for WebRTC streams, so that
			// counter never armed this watchdog there, and currentTime keeps
			// advancing on a dead stream in both browsers (measured Oct 2026).
			if (video !== watched && video.requestVideoFrameCallback) {
				watched = video;
				const onFrame = () => {
					if (watched !== video) return;
					presented += 1;
					video.requestVideoFrameCallback(onFrame);
				};
				video.requestVideoFrameCallback(onFrame);
			}
			// Fallbacks for browsers without rVFC: decoded-frame counters, then
			// currentTime.
			const frames = watched
				? presented
				: video.getVideoPlaybackQuality?.().totalVideoFrames ??
					video.webkitDecodedFrameCount ??
					null;
			const sample = frames !== null ? frames : video.currentTime;
			if (sample !== lastSample) {
				lastSample = sample;
				if (sample > 0) {
					sawMedia = true;
					mediaSince ??= Date.now();
					if (Date.now() - mediaSince >= HEALTHY_MS) autoReconnectsRef.current = 0;
				}
				stallSince = null;
				setStreamStalled(false);
				return;
			}
			if (!sawMedia) return; // never started — the connect UI owns that case
			if (stallSince === null) {
				stallSince = Date.now();
				return;
			}
			if (Date.now() - stallSince >= STALL_MS) setStreamStalled(true);
		}, 1000);
		return () => {
			timers.clearInterval(iv);
			watched = null; // stops the rVFC chain
		};
	}, [streamPath]);

	// Probe the device only while something is wrong (stream stalled, connect
	// failed, or the polled status says offline). The live TCP dial outranks
	// the polled Tailscale status, which can lag a drop — in either direction
	// — by minutes.
	const probeReachable = useHostReachability(
		ip,
		streamStalled || !deviceOnline || Boolean(error)
	);
	const deviceLost =
		probeReachable === false || (!deviceOnline && probeReachable !== true);

	// Restart Sunshine on the devkit over SSH, then reconnect fresh — the
	// go-to remedy for black screens / stuck sessions.
	const handleRestartSunshine = async () => {
		setRestarting(true);
		setError(null);
		setStreamPath(null);
		setStage('checking');
		try {
			await restartSunshine(ip, devkit?.devkit_id);
			await connect();
		} catch (e) {
			setError(e.message || 'Could not restart Sunshine');
		} finally {
			setRestarting(false);
		}
	};

	const toggleDetails = () => {
		try {
			const doc = iframeRef.current?.contentDocument;
			if (!doc) return;
			const next = !showDetails;
			doc.documentElement.classList.toggle('mw-details', next);
			setShowDetails(next);
		} catch {
			// cross-origin — nothing to toggle
		}
	};

	const connect = useCallback(async () => {
		setError(null);
		setStreamPath(null);
		setStage('checking');
		try {
			const { path, hostId: id } = await resolveStream(ip, {
				admin,
				onStage: setStage,
			});
			setHostId(id);
			setStreamPath(path);
		} catch (e) {
			setError(e.message || 'Could not start the stream');
		}
	}, [ip, admin]);

	// Stop the app session on the devkit (frees it up for pairing/others),
	// then close the overlay. Sunshine's quit-app (/cancel) terminates EVERY
	// stream session on the host, not just ours, and logs nothing at Info —
	// a second viewer's End Session silently froze the first viewer's stream
	// (1002, Oct 2026). Confirm in the popup (a dashboard-tab dialog would be
	// hidden behind it).
	const endSession = async () => {
		const popup = iframeRef.current?.ownerDocument?.defaultView || window;
		if (!popup.confirm(
			'End Session quits the desktop session on this devkit and disconnects ' +
			'EVERYONE streaming it.\n\nTo close only your window, use × instead.'
		)) return;
		setEnding(true);
		try {
			await endStreamSession(hostId);
		} catch {
			// closing anyway — worst case the session stays resumable
		}
		onClose();
	};

	// Default the embedded moonlight-web client to pointAndDrag mouse mode:
	// its default ("follow") sends absolute positions, which Sunshine on the
	// Linux devkits presents as a touchscreen — click-drags become touch
	// scrolls, so map panning in ProteusCore never works. pointAndDrag
	// positions the cursor absolutely on click but streams RELATIVE motion
	// while a button is held (a real mouse drag on the device). moonlight-web
	// reads settings from same-origin localStorage ("mlSettings"), so seed
	// the mode only when the user hasn't picked one in the stream sidebar.
	useEffect(() => {
		try {
			const saved = JSON.parse(localStorage.getItem('mlSettings') || '{}');
			if (!saved.mouseMode) {
				localStorage.setItem(
					'mlSettings',
					JSON.stringify({ ...saved, mouseMode: 'pointAndDrag' })
				);
			}
		} catch { /* corrupt settings: leave them alone */ }
	}, []);

	useEffect(() => {
		if (ip) connect();
	}, [ip, connect]);

	// Auto-reconnect a stream that died while the devkit still answers
	// (Sunshine session ended — e.g. another viewer's End Session — or a
	// WebRTC hiccup). Waits for the reachability probe so a dead device gets
	// DeviceOfflineOverlay instead (which reconnects on its own when the
	// device returns). Backs off 2s/4s/8s and gives up after 3 tries until
	// the stream has played for a minute, leaving the manual Reconnect banner
	// — unbounded retries are how x11vnc got wedged by reconnect storms.
	// Popup timers for the same throttling reason as the stall watchdog.
	const MAX_AUTO_RECONNECTS = 3;
	useEffect(() => {
		if (!streamStalled || probeReachable !== true) return undefined;
		if (autoReconnectsRef.current >= MAX_AUTO_RECONNECTS) return undefined;
		const timers = iframeRef.current?.ownerDocument?.defaultView || window;
		const t = timers.setTimeout(() => {
			autoReconnectsRef.current += 1;
			connect();
		}, 2000 * 2 ** autoReconnectsRef.current);
		return () => timers.clearTimeout(t);
	}, [streamStalled, probeReachable, connect]);
	const autoReconnecting = autoReconnectsRef.current < MAX_AUTO_RECONNECTS;

	// Device lost: DeviceOfflineOverlay explains the frozen frame. When the
	// device comes back, the WebRTC session didn't survive the outage —
	// resolve a fresh stream automatically.
	const wasOfflineRef = useRef(false);
	useEffect(() => {
		if (deviceLost) {
			wasOfflineRef.current = true;
			return;
		}
		if (wasOfflineRef.current) {
			wasOfflineRef.current = false;
			if (ip) connect();
		}
	}, [deviceLost, ip, connect]);

	const injectDashboardTheme = (e) => {
		try {
			const doc = e.target.contentDocument;
			if (!doc?.head) return;
			const style = doc.createElement('style');
			style.textContent = DASHBOARD_THEME_CSS;
			doc.head.appendChild(style);
			// re-apply the Details state across page reloads
			doc.documentElement.classList.toggle('mw-details', showDetails);
		} catch {
			// cross-origin (shouldn't happen — same-origin via nginx); stock theme stays
		}
	};

	return (
		<WindowPortal title={`Moonlight Stream — ${name}`} onClose={onClose}>
		<div className='fixed inset-0 z-50 flex flex-col bg-black'>
			{/* Title bar — matches the dashboard header */}
			<div className='flex items-center justify-between px-4 sm:px-6 py-3 bg-gradient-to-r from-gray-800 via-gray-900 to-black border-b border-gray-700 shadow-md flex-shrink-0'>
				<div className='flex items-center gap-3 min-w-0'>
					<div className='p-2 rounded-lg bg-blue-500/20'>
						<svg className='w-4 h-4 text-blue-400' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
							<path strokeLinecap='round' strokeLinejoin='round' d='M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2zM10 7.5l4.5 2.5-4.5 2.5v-5zM9 21h6' />
						</svg>
					</div>
					<div className='min-w-0'>
						<div className='text-sm font-semibold text-white truncate'>
							Moonlight Stream — {name}
						</div>
						<div className='text-xs text-slate-400 truncate'>
							{ip} · Sunshine
							{streamPath && (
								<span className='ml-2 inline-flex items-center gap-1 text-emerald-400'>
									<span className='w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block'></span>
									Live
								</span>
							)}
						</div>
					</div>
				</div>
				<div className='flex items-center gap-2 flex-shrink-0'>
					{admin && (
						<button
							onClick={handleRestartSunshine}
							disabled={restarting}
							className='px-3 py-1.5 text-xs font-medium rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 border border-white/10 transition-colors disabled:opacity-50'
							title='Restart the Sunshine service on the devkit (fixes stuck sessions / black screens), then reconnect'
						>
							{restarting ? 'Restarting…' : 'Restart Sunshine'}
						</button>
					)}
					{streamPath && (
						<button
							onClick={toggleDetails}
							className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
								showDetails
									? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
									: 'bg-white/5 hover:bg-white/10 text-gray-300 border-white/10'
							}`}
							title='Show connection log and stream settings'
						>
							Details
						</button>
					)}
					{streamPath && hostId != null && (
						<button
							onClick={endSession}
							disabled={ending}
							className='px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/30 transition-colors disabled:opacity-50'
							title='Stop the session on the devkit for everyone streaming it, and close'
						>
							{ending ? 'Ending…' : 'End Session'}
						</button>
					)}
					<button
						onClick={onClose}
						className='text-white hover:bg-white/20 rounded-full p-2 transition-colors'
						title='Close (session stays resumable)'
					>
						<svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
							<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
						</svg>
					</button>
				</div>
			</div>

			{/* Body */}
			{streamPath ? (
				<iframe
					ref={iframeRef}
					src={`/sunshine/${streamPath}`}
					title={`Moonlight Stream — ${name}`}
					className='flex-1 w-full border-0'
					allow='autoplay; fullscreen; gamepad; clipboard-read; clipboard-write; pointer-lock'
					allowFullScreen
					onLoad={injectDashboardTheme}
				/>
			) : (
				<div className='flex-1 flex items-center justify-center p-4'>
					{error ? (
						<div className='bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-700/50 p-8 max-w-md w-full text-center'>
							<div className='w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4'>
								<svg className='w-7 h-7 text-red-400' fill='none' stroke='currentColor' viewBox='0 0 24 24' strokeWidth={2}>
									<path strokeLinecap='round' strokeLinejoin='round' d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' />
								</svg>
							</div>
							<h3 className='text-lg font-semibold text-white'>Stream unavailable</h3>
							<p className='text-sm text-gray-400 mt-2 break-words'>{error}</p>
							<div className='flex gap-3 mt-6'>
								<button
									onClick={connect}
									className='flex-1 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors'
								>
									Try Again
								</button>
								{admin && (
									<button
										onClick={handleRestartSunshine}
										disabled={restarting}
										className='flex-1 px-4 py-2.5 rounded-lg bg-slate-700/80 hover:bg-slate-600/80 text-gray-200 text-sm font-medium transition-colors disabled:opacity-50'
									>
										{restarting ? 'Restarting…' : 'Restart Sunshine'}
									</button>
								)}
								<button
									onClick={onClose}
									className='flex-1 px-4 py-2.5 rounded-lg bg-slate-700/80 hover:bg-slate-600/80 text-gray-200 text-sm font-medium transition-colors'
								>
									Close
								</button>
							</div>
						</div>
					) : (
						<div className='flex flex-col items-center gap-5 text-center'>
							<div className='sunshine-spinner' />
							<div>
								<div className='text-white font-medium'>
									{restarting
										? 'Restarting Sunshine on the devkit…'
										: STAGE_TEXT[stage] || 'Connecting…'}
								</div>
								{stage === 'pairing' && (
									<div className='text-xs text-slate-400 mt-1'>
										First connection to this devkit — this takes a few seconds
									</div>
								)}
							</div>
							<style>{`
								@keyframes sunshine-spin { to { transform: rotate(360deg); } }
								.sunshine-spinner {
									width: 44px; height: 44px; border-radius: 50%;
									border: 4px solid #374151; border-top-color: #3b82f6;
									animation: sunshine-spin 0.9s linear infinite;
								}
							`}</style>
						</div>
					)}
				</div>
			)}

			{/* Stall banner: frames stopped but the devkit answers TCP — a
			    Sunshine/WebRTC hiccup, not an outage. Non-blocking (the stream
			    may recover by itself); clears when frames resume. */}
			{streamStalled && !deviceLost && streamPath && (
				<div className='absolute top-20 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-2.5 rounded-xl bg-slate-900/95 border border-slate-700/50 shadow-2xl'>
					<span className='w-2 h-2 rounded-full bg-amber-400 animate-pulse inline-block flex-shrink-0' />
					<span className='text-sm text-gray-200'>
						{autoReconnecting
							? 'Stream interrupted — reconnecting…'
							: 'Stream interrupted — the device is still reachable'}
					</span>
					<button
						onClick={connect}
						className='px-3 py-1 text-xs font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors flex-shrink-0'
					>
						Reconnect
					</button>
				</div>
			)}

			{deviceLost && (
				<DeviceOfflineOverlay
					deviceName={`Devkit ${deviceId}`}
					onClose={onClose}
				/>
			)}
		</div>
		</WindowPortal>
	);
};

export default SunshineView;
